/**
 * Shared Effect runtime for the dashboard API layer (`src/api/*`).
 *
 * Mirrors `src/features/tools/runners/effect-runtime.ts`: each module extracts
 * its real async seam (Clerk token bridge, header preparation, fetch lifecycle,
 * response parsing, uploads, RTK Query queryFn bodies) into a typed `Effect`
 * program, and the public Promise/sync surface stays a thin adapter over
 * `runApiPromise`/`runApiSync`.
 *
 * Failures are REDACTED: the serializable error value carries an operation kind
 * and a short static subject label only. Response bodies, server error strings,
 * tokens, URLs, document ids, and user content never reach it. The original
 * cause is kept in a side WeakMap so the adapters re-throw exactly what the
 * underlying API threw — keeping legacy rejection messages byte-for-byte
 * identical.
 */
import { Data, Effect } from 'effect';

export type ApiOperation =
  /** Clerk token bridge and auth-header preparation. */
  | 'token'
  /** Network request lifecycle (fetch, RTK fetchWithBQ). */
  | 'fetch'
  /** Response body parsing and validation. */
  | 'parse'
  /** Document upload lifecycle. */
  | 'upload'
  /** Browser popup / navigation lifecycle. */
  | 'browser';

/**
 * Redacted API-layer failure. Only the operation kind and a static subject
 * label live on the error value; nothing derived from user content, server
 * payloads, or tokens does.
 */
export class ApiRuntimeError extends Data.TaggedError('ApiRuntimeError')<{
  readonly operation: ApiOperation;
  readonly subject: string;
}> {}

const apiRuntimeCauses = new WeakMap<ApiRuntimeError, unknown>();

function apiRuntimeError(
  operation: ApiOperation,
  subject: string,
  cause: unknown,
): ApiRuntimeError {
  const error = new ApiRuntimeError({ operation, subject });
  apiRuntimeCauses.set(error, cause);
  return error;
}

/** Lift a synchronous throwing call into a redacted-failure program. */
export function attemptApi<A>(
  operation: ApiOperation,
  subject: string,
  evaluate: () => A,
): Effect.Effect<A, ApiRuntimeError> {
  return Effect.try({
    try: evaluate,
    catch: (cause) => apiRuntimeError(operation, subject, cause),
  });
}

/** Lift a promise-returning call into a redacted-failure program. */
export function attemptApiPromise<A>(
  operation: ApiOperation,
  subject: string,
  evaluate: () => PromiseLike<A>,
): Effect.Effect<A, ApiRuntimeError> {
  return Effect.tryPromise({
    try: evaluate,
    catch: (cause) => apiRuntimeError(operation, subject, cause),
  });
}

/** Fail with a redacted error whose original cause rides the side registry. */
export function failApi(
  operation: ApiOperation,
  subject: string,
  cause: unknown,
): Effect.Effect<never, ApiRuntimeError> {
  return Effect.fail(apiRuntimeError(operation, subject, cause));
}

/**
 * Swap the redacted error back for the original cause, so callers keep seeing
 * the exact `Error` (and message) the underlying API produced.
 */
export function restoreApiRuntimeError<A>(
  program: Effect.Effect<A, ApiRuntimeError>,
): Effect.Effect<A, unknown> {
  return program.pipe(Effect.catch((error) => Effect.fail(apiRuntimeCauses.get(error) ?? error)));
}

/** Read the redacted cause without leaking it into the error value's fields. */
export function apiRuntimeCause(error: ApiRuntimeError): unknown {
  return apiRuntimeCauses.get(error);
}

export function runApiSync<A>(program: Effect.Effect<A, ApiRuntimeError>): A {
  return Effect.runSync(restoreApiRuntimeError(program));
}

export function runApiPromise<A>(
  program: Effect.Effect<A, ApiRuntimeError>,
  options?: { signal?: AbortSignal },
): Promise<A> {
  return Effect.runPromise(restoreApiRuntimeError(program), options);
}
